import React from 'react';
import {
  useHierarchicalMenu,
  UseHierarchicalMenuProps,
} from 'react-instantsearch';
import { HierarchicalMenuItem } from 'instantsearch.js/es/connectors/hierarchical-menu/connectHierarchicalMenu';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';

import IconWrapper from '@components/ui/Icons';
import { EventName, trackNavClick } from '@utils/gtm/ga4/trackNavEvent';
import { PRODUCTS, PRODUITS } from '@utils/constants';

import styles from './CategoryNav.module.scss';

type HierarchicalListProps = Pick<
  ReturnType<typeof useHierarchicalMenu>,
  'items' | 'createURL'
>;

const findActiveCategory = (categoryItems: HierarchicalMenuItem[]) => {
  let activeCategory = null;

  const traverse = (data: HierarchicalMenuItem[]) => {
    for (const item of data) {
      if (item.isRefined === true) {
        activeCategory = item.label;
      }
      if (item.data && item.data.length > 0) {
        traverse(item.data);
      }
    }
  };

  traverse(categoryItems);

  return activeCategory;
};

const HierarchicalList = ({ items, createURL }: HierarchicalListProps) => {
  if (items.length === 0) {
    return null;
  }

  const handleClick = (label: string, path: string) => {
    trackNavClick({
      data: {
        eventName: EventName.clickCategoryNavigation,
        linkUrl: path ?? null,
        linkText: label,
      },
    });
  };

  const menuIsRefined = items.some((item) => item.isRefined);
  const activeCategory = findActiveCategory(items);

  return (
    <ul
      className={cn(styles.menuList, {
        [styles.menuIsRefined]: menuIsRefined,
      })}
    >
      {items.map((item) => {
        const refinedChild = item.data?.find(({ isRefined }) => isRefined);
        const valueToRefine = refinedChild ? refinedChild.value : item.value;

        // Clean up double locale in URL and remove page number
        const url = createURL(valueToRefine);
        const cleanUrl = url
          .replace(/\/(en|fr)\/\1\//, '/$1/')
          .replace(/\?page=\d+/, '');

        return (
          <li
            key={item.value}
            className={cn({
              [styles.menuParentItem]: item.isRefined,
              [styles.menuParentItemInactive]: !item.isRefined,
            })}
          >
            {item.label === activeCategory ? (
              <span
                className={cn(styles.menuLink, styles.menuSelectedItemLink, {
                  [styles.menuActiveItemLink]: item.isRefined,
                })}
              >
                {item.label}
              </span>
            ) : (
              <a
                href={cleanUrl}
                onClick={() =>
                  handleClick(item.label, createURL(valueToRefine))
                }
                rel="prefetch"
                className={cn(styles.menuLink, {
                  [styles.menuActiveItemLink]: item.isRefined,
                })}
              >
                {item.isRefined && (
                  <IconWrapper
                    classNames={styles.menuActiveIcon}
                    icon="caret-left"
                    width={12}
                    height={12}
                  />
                )}
                <span className={styles.menuLabel}>{item.label}</span>
                <span className={styles.menuCount}> ({item.count})</span>
              </a>
            )}

            {item.data && (
              <HierarchicalList items={item.data} createURL={createURL} />
            )}
          </li>
        );
      })}
    </ul>
  );
};

const ViewAllLink = ({ locale }: { locale?: string }) => {
  const { t } = useTranslation(['searchResults']);
  return (
    <a
      href={`/${locale}/${locale === 'fr' ? PRODUITS : PRODUCTS}`}
      className={styles.viewAllLink}
      rel="prefetch"
    >
      <IconWrapper
        classNames={styles.menuActiveIcon}
        icon="caret-left"
        width={12}
        height={12}
      />
      <span>{t('search.viewAll')}</span>
    </a>
  );
};

type CategoryNavProps = UseHierarchicalMenuProps & {
  locale?: string;
};

const CategoryNav = (props: CategoryNavProps) => {
  const { locale, ...hierarchicalMenuProps } = props;
  const { items, createURL } = useHierarchicalMenu(hierarchicalMenuProps);

  const showViewAll = items.some((item) => item.isRefined);

  return (
    <>
      {showViewAll ? <ViewAllLink locale={locale} /> : null}
      <div className={styles.menuRoot}>
        <HierarchicalList items={items} createURL={createURL} />
      </div>
    </>
  );
};

export default CategoryNav;
